import { z } from "zod";

export interface NoteInfo {
  id: string;
  projectId: string | null;
  projectName: string | null;
  body: string;
  hasAudio: boolean;
  audioSeconds: number | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  /** Darf die angemeldete Person die Notiz ändern und löschen? */
  canEdit: boolean;
}

export interface EventInfo {
  id: string;
  projectId: string | null;
  projectName: string | null;
  title: string;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  location: string | null;
  details: string | null;
  assignedTo: string | null;
  assignedName: string | null;
  /** Herkunft, z. B. "outlook", sobald die Kalender-Anbindung steht; sonst null. */
  source: string | null;
}

export const NOTE_MAX_CHARS = 10_000;
export const AUDIO_MAX_MB = 25;
export const AUDIO_MAX_MINUTES = 10;

/** Erlaubte Formate für Sprachmemos (Chrome/Android nimmt WebM auf, iPhone MP4). */
export const AUDIO_TYPES: Record<string, string> = {
  "audio/webm": "audio/webm",
  "audio/ogg": "audio/ogg",
  "audio/mp4": "audio/mp4",
  "audio/x-m4a": "audio/mp4",
  "audio/aac": "audio/aac",
  "audio/mpeg": "audio/mpeg",
  "video/webm": "audio/webm",
};

export function audioMime(raw: string | undefined): string | undefined {
  return AUDIO_TYPES[(raw ?? "").split(";")[0]!.trim().toLowerCase()];
}

const optionalId = z.uuid().nullable().optional();

export const noteSchema = z.object({
  body: z.string().max(NOTE_MAX_CHARS, `Eine Notiz kann höchstens ${NOTE_MAX_CHARS} Zeichen lang sein.`).default(""),
  projectId: optionalId,
});

export const noteUpdateSchema = z.object({
  body: z.string().max(NOTE_MAX_CHARS, `Eine Notiz kann höchstens ${NOTE_MAX_CHARS} Zeichen lang sein.`).optional(),
  projectId: optionalId,
});

const isoDate = z.iso.datetime({ offset: true, error: "Bitte geben Sie ein gültiges Datum an." });

export const eventSchema = z
  .object({
    title: z.string().trim().min(1, "Bitte geben Sie dem Termin einen Titel.").max(200, "Der Titel ist zu lang."),
    startsAt: isoDate,
    endsAt: isoDate,
    allDay: z.boolean().default(false),
    location: z.string().trim().max(200).nullable().optional(),
    details: z.string().trim().max(5000).nullable().optional(),
    projectId: optionalId,
    assignedTo: optionalId,
  })
  .refine((e) => new Date(e.endsAt) >= new Date(e.startsAt), { message: "Das Ende liegt vor dem Beginn.", path: ["endsAt"] });

export const eventUpdateSchema = z.object({
  title: z.string().trim().min(1, "Bitte geben Sie dem Termin einen Titel.").max(200, "Der Titel ist zu lang.").optional(),
  startsAt: isoDate.optional(),
  endsAt: isoDate.optional(),
  allDay: z.boolean().optional(),
  location: z.string().trim().max(200).nullable().optional(),
  details: z.string().trim().max(5000).nullable().optional(),
  projectId: optionalId,
  assignedTo: optionalId,
});

/** Stand der Verbindung mit dem eigenen Outlook-Kalender. */
export interface OutlookStatus {
  /** HandVero hat die Anbindung eingerichtet (App bei Microsoft registriert). */
  available: boolean;
  connected: boolean;
  accountEmail: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
}

/** Einen Termin per E-Mail an jemanden außerhalb von HandVero schicken (z. B. Kunde). */
export const eventShareSchema = z.object({
  email: z.string().trim().min(3, "Bitte geben Sie eine E-Mail-Adresse an.").max(254),
  message: z.string().trim().max(1000, "Die Nachricht ist zu lang.").optional(),
  /** Notiz des Termins mitschicken? Sonst nur Titel, Zeit und Ort. */
  withDetails: z.boolean().default(false),
});

export type CalendarFeedScope = "alle" | "meine";

export const CALENDAR_FEED_SCOPES: Record<CalendarFeedScope, string> = {
  alle: "Alle Termine, die ich in HandVero sehe",
  meine: "Nur meine Termine",
};

/** Kalender-Abo: geheimer Link, den Outlook, Google oder das iPhone regelmäßig abrufen. */
export interface CalendarFeedInfo {
  active: boolean;
  scope: CalendarFeedScope;
  withDetails: boolean;
  /** https-Adresse zum Kopieren (z. B. für Google Kalender). */
  url: string | null;
  /** webcal-Adresse: öffnet Outlook oder den Apple-Kalender direkt. */
  webcal: string | null;
  lastUsedAt: string | null;
}

export const calendarFeedSchema = z.object({
  scope: z.enum(["alle", "meine"]),
  withDetails: z.boolean(),
});
